/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import _ from 'lodash';
import { View } from 'backbone.marionette';

import SelectRowCell from '@/backgrid/SelectRowCell';
import GridView from '@/views/GridView';
import PageableCollection from '@/backbone/PageableCollection';
import lazyLoadView from '@/util/lazyLoadView';

import SearchCommands from '../../../common/services/SearchCommands';
import GetAllRegions from '../../../common/services/GetAllRegions';
import GetAllServiceBranches from '../../../common/services/GetAllServiceBranches';
import loadSearchFields from '../../loaders/SearchFieldsViewLoader';

import './SelectCommandModalView.css';

import tmplSelectCommandModalView from './SelectCommandModalView.hbs';

const SelectCommandModalView = View.extend({
  template: tmplSelectCommandModalView,

  className: 'select-command-modal-view',

  ui: {
    searchFieldsView: '.search-fields-view',
    commandsGrid: '#commands-grid',
    btnCancel: '.btn-cancel',
    btnSelectCommands: '.btn-select-commands'
  },

  regions: {
    commandsGrid: '@ui.commandsGrid',
    searchFieldsView: '@ui.searchFieldsView'
  },

  events: {
    'click @ui.btnSelectCommands': 'onClickSelectCommands',
    'click @ui.btnCancel': 'onClickCancel'
  },

  childViewEvents: {
    'search:response': 'onSearchResponse'
  },

  onSearchResponse(response) {
    this.commandsGrid.collection.fullCollection.reset(response.commands);
    this.getChildView('commandsGrid').focusOnGrid();
  },

  onRender() {
    const searchFields = [
      {
        id: 'dodac-search-field',
        name: 'dodac',
        class: 'InputSearchField',
        placeholder: 'DODAAC'
      },
      {
        id: 'command-name-search-field',
        name: 'commandName',
        class: 'InputSearchField',
        placeholder: 'Command Name'
      },
      {
        id: 'service-branch-id-search-field',
        name: 'serviceBranchID',
        class: 'SelectSearchField',
        placeholder: 'Branch',
        lookupService: GetAllServiceBranches
      },
      {
        id: 'region-id-search-field',
        name: 'regionID',
        class: 'SelectSearchField',
        placeholder: 'Location',
        lookupService: GetAllRegions,
        lookupFilter: (region) => region.isSpecialty === 'false'
      }
    ];

    this.showChildView(
      'searchFieldsView',
      lazyLoadView(() => loadSearchFields(searchFields, SearchCommands))
    );

    this.commandsGrid = new GridView({
      columns: [
        {
          name: 'selected',
          label: '',
          cell: SelectRowCell.SelectRowCell,
          headerCell: SelectRowCell.SelectAllHeaderCell,
          width: 10,
          editable: true,
          sortable: false
        },
        {
          name: 'dodac',
          label: 'DODAAC',
          cell: 'string',
          headerCell: 'custom',
          width: 60,
          editable: false,
          sortable: true
        },
        {
          name: 'name',
          label: 'Command',
          cell: 'string',
          headerCell: 'custom',
          width: 60,
          editable: false,
          sortable: true
        },
        {
          name: 'serviceBranchName',
          label: 'Branch',
          cell: 'string',
          headerCell: 'custom',
          width: 30,
          editable: false,
          sortable: true
        },
        {
          name: 'regionName',
          label: 'Location',
          cell: 'string',
          headerCell: 'custom',
          width: 30,
          editable: false,
          sortable: true
        }
      ],
      collection: new PageableCollection([], { pageSize: 5 }),
      emptyText: 'No Commands Found'
    });

    this.showChildView('commandsGrid', this.commandsGrid);
  },

  toggleForm(state) {
    this.$el.find(':input').prop('disabled', !state);
  },

  onClickCancel(e) {
    e.preventDefault();
    this.triggerMethod('modal:close', []);
  },

  onClickSelectCommands(e) {
    e.preventDefault();
    const selectedModels = this.commandsGrid.viewGrid.getSelectedModels();

    const commandsToAdd = _.reduce(
      selectedModels,
      (acc, x) => {
        if (x) {
          acc.push(x.toJSON());
        }
        return acc;
      },
      []
    );

    if (this.model && (this.model.get('isProfilePage') || this.model.get('isSelfService'))) {
      _.each(commandsToAdd, (command) => {
        command.approvalStatusID = 1;
        command.isNewRequest = true;
      });
    }

    this.triggerMethod('modal:close', commandsToAdd);
  }
});

export default SelectCommandModalView;
